import React, {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  forwardRef,
  useEffect,
  Profiler,
} from "react";
import ReactDOMServer from "react-dom/server";
import { OrgChart } from "d3-org-chart";
import CustomExpandButton from "./ChartButton/customExpandButton";
import CustomNodeContent from "./ChartNode/CustomNode";
import FitButton from "./ChartButton/fitButton";
import SwapButton from "./ChartButton/swapButton";
import SetCenteredButton from "./ChartButton/setCentered";
import * as d3 from "d3";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import EmployeeDetailsCard from "./ChartNode/employeeDetailsCard";
import EditButton from "./ChartButton/editButton";
import PDFExportButton from "./ChartButton/PDFExportButton";
import SaveButton from "./ChartButton/saveButton";
import HidePrivateInfoButton from "./ChartButton/hidePrivateInfoButton";
import AddNodeModal from "./modal/addNodeModal";
import MoveNodeModal from "./modal/moveNodeModal";
import {
  Add,
  Delete,
  Edit,
  MoveDown,
  PropaneTwoTone,
  UnfoldMore,
} from "@mui/icons-material";
import EditNodeModal from "./modal/editNodeModal";
import DeleteNodeModal from "./modal/deleteNodeModal";
import AlertPopup from "./modal/alertPopup";
import { I18n } from "aws-amplify/utils";
import ConfirmSaveModal from "./modal/confirmSaveSimulationModal";
import ConfirmSaveSimulationModal from "./modal/confirmSaveSimulationModal";
import ExpandAllButton from "./ChartButton/expandAllButton";
import CollapseAllButton from "./ChartButton/collapseAllButton";
import { hrPostNewSimulation } from "../apiService";
import dayjs from "dayjs";
import FullscreenLoader from "components/FullscreenLoader";
import { loadOrgData, saveOrgData } from "../indexedDbUtils";
import pako from "pako";

const OrganizationalChart = forwardRef((props, ref) => {
  const { data, period } = props;
  const d3Container = useRef(null); //riferimento al container del grafico
  const chartRef = useRef(new OrgChart()); //riferimento per l'istanza di OrgChart
  const [cardShow, setCardShow] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [chartError, setChartError] = useState(false);
  // const [layoutIndex, setLayoutIndex] = useState(1); //per far partire da "top"
  const [layoutIndex, setLayoutIndex] = useState(() => {
    const saved = localStorage.getItem("orgChartLayoutIndex");
    return saved !== null ? parseInt(saved, 10) : 1;
  }); //per far partire da "top"
  const [isEditMode, setIsEditMode] = useState(false);
  const [highlightedUser, setHighlightedUser] = useState(true);
  const [_, forceRerender] = useState(false);

  const [modifiedNodeIds, setModifiedNodeIds] = useState([]); //array contenente gli id dei nodi modificati tramite edit
  const [addedNodeIds, setAddedNodeIds] = useState([]);
  const [orgData, setOrgData] = useState(null); //stato che contiene i dati del grafico

  const [contextMenu, setContextMenu] = useState(null); //stato per il menu
  const [selectedNode, setSelectedNode] = useState(null); //nodo selezionato

  //stati per le modali
  const [openAddModal, setOpenAddModal] = useState(false); //stato apertura modal add
  const [openMoveModal, setOpenMoveModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [confirmSaveOpenModal, setConfirmSaveOpenModal] = useState(false);
  const [isPrivateHidden, setIsPrivateHidden] = useState(true);
  const [loggedUserData, setLoggedUserData] = useState({...props.loggedUserData, disablePrivateInfo: false, reports: [], reportsIds: []});

  //funzione per chiudere il menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // const nextIdRef = useRef(
  //   Math.max(0, ...data.map((n) => (typeof n.id === "number" ? n.id : 0))) + 1
  // );

  const showAlert = (message, severity) => {
    setAlertTitle(severity);
    setAlertMessage(message);
    setOpenAlert(true);
    setTimeout(() => {
      setOpenAlert(false);
    }, 3000);
  };

  const handleAddNode = async (newNode) => {
    const updatedData = [...orgData, newNode];
    setOrgData(updatedData);
    setModifiedNodeIds((prev) => [...new Set([...prev, newNode.id])]);
    // localStorage.setItem("orgData", JSON.stringify(updatedData));
    await saveOrgData(updatedData);
    showAlert(I18n.get("Node added successfully"), I18n.get("Success"));
  };

  useEffect(() => {
    setOrgData(data);
    const loggedUser = props.loggedUserData ?? null;
    const loggedUserRole = loggedUser.user_role ?? null;
    const userId = loggedUser.id ?? null;
    const userCountry = loggedUser.country_name;
    const userVisibilities = {
      ...loggedUser,
      disablePrivateInfo: false,
      reports: [],
      reportsIds: []
    };
    
    if (loggedUserRole && loggedUserRole === "manager") {
      const dataMap = new Map();
      const queue = [userId];
      const managerReports = [];
      const managerReportsIds = [userId];
      
      for (const node of data) {
        if (node.parentId) {
          if (!dataMap.has(node.parentId)) {
            dataMap.set(node.parentId, []);
          }
          dataMap.get(node.parentId).push(node);
        }

        // if( node.id && node.id === userId){
        //   managerReports.push(node);
        // }
      }
    
      while (queue.length > 0) {
        const currentId = queue.shift();
        const children = dataMap.get(currentId) || [];

        for (const child of children) {
          // managerReports.push(child);
          managerReportsIds.push(child.id);
          queue.push(child.id);
        }
      }

      if (userCountry && userCountry === "United States") {
        userVisibilities["disablePrivateInfo"] = true;
      }

      // userVisibilities["reports"] = managerReports;
      userVisibilities["reportsIds"] = managerReportsIds;
      
    }
    console.log(userVisibilities);
    setLoggedUserData(userVisibilities);

  }, [data, props.loggedUserData]);


  const handleOpenAddModal = () => {
    if (contextMenu?.nodeData) {
      setSelectedNode(contextMenu.nodeData); //nodo genitore
    }
    setOpenAddModal(true);
    handleCloseContextMenu();
  };

  const handleOpenMoveNode = () => {
    setOpenMoveModal(true);
    handleCloseContextMenu();
  };

  const handleOpenDeleteModal = () => {
    if (!contextMenu?.nodeData) {
      //console.log("nessun nodo selezionato");
      return;
    }
    const nodeToDelete = contextMenu.nodeData;

    const hasChildren = orgData.some((n) => n.parentId === nodeToDelete.id);

    if (hasChildren) {
      setOpenAlert(true);
      setAlertTitle(I18n.get("Action not permitted"));
      setAlertMessage(
        I18n.get(
          "Please, remove all subordinate nodes before proceeding with the deletion"
        )
      );
      handleCloseContextMenu();
      return;
    }
    setSelectedNode(nodeToDelete);
    setOpenDeleteModal(true);
    handleCloseContextMenu();
  };

  const handleDeleteNode = async () => {
    if (!selectedNode) {
      //console.log("nessun nodo selezionato");
      return;
    }
    const hasChildren = orgData.some(
      (node) => node.parentId === selectedNode.id
    );
    if (hasChildren) {
      alert(I18n.get("questo nodo non può essere eliminato"));
      setOpenDeleteModal(false);
      return;
    } else {
      const updatedData = orgData.filter((node) => node.id !== selectedNode.id);
      setOrgData(updatedData);
      // localStorage.setItem("orgData", JSON.stringify(updatedData));
      await saveOrgData(updatedData);
      showAlert(I18n.get("Node deleted successfully"), I18n.get("Success"));
      setOpenDeleteModal(false);
      setSelectedNode(null);
    }
  };
  const handleOpenEditModal = () => {
    if (!contextMenu?.nodeData) {
      //console.log("nessun nodo selezionato");
      return;
    }
    setOpenEditModal(true);
    setTimeout(() => {
      handleCloseContextMenu();
    }, 0);
    const node = contextMenu.nodeData; //creo un nuovo oggetto per forzare l'aggiornamento
    setSelectedNode(node);
    //console.log("selectedNode impostato a: ", node);
  };

  const handleEditSubmit = async (updatedNode) => {
    setOrgData((prevData) =>
      prevData.map((node) =>
        node.id === selectedNode.id
          ? { ...node, ...updatedNode, isModified: true }
          : node
      )
    );
    setModifiedNodeIds((prev) => [...new Set([...prev, selectedNode.id])]);
    const updated = orgData.map((node) =>
      node.id === selectedNode.id ? { ...node, ...updatedNode } : node
    );
    await saveOrgData(updated);
    // localStorage.setItem("orgData", JSON.stringify(updated));
    showAlert(I18n.get("Changes saved successfully"), I18n.get("Success"));
    setOpenEditModal(false);
  };

  //funzione per aggiornare i dati dopo l'aggiunta di un nodo
  // const handleSubmitAdd = (newNode) => {
  //   setOrgData((prevData) => [...prevData, newNode]);
  //   setOpenAddModal(false);
  // };

  const handleMove = async (nodeId, newParentId) => {
    const updatedData = orgData.map((node) =>
      node.id === nodeId
        ? { ...node, parentId: newParentId, isModified: true }
        : node
    );
    setOrgData(updatedData);
    setModifiedNodeIds((prev) => [...new Set([...prev, nodeId])]);
    await saveOrgData(updatedData);
    // localStorage.setItem("orgData", JSON.stringify(updatedData));
    showAlert(I18n.get("Node moved successfully"), "Success");
    setOpenMoveModal(false);
  };

  const handleShow = () => setCardShow(true);
  const handleClose = () => setCardShow(false);

  const handleHidePrivate = () => {
    setIsPrivateHidden(!isPrivateHidden);
  };

  // const handleIsPrivateBtnDisabled = () => {
  //   const loggedUserData = props.loggedUserData;
  //   if (loggedUserData && loggedUserData.country_name) {
  //     return loggedUserData.country_name === 'United States' && loggedUserData.user_role === 'manager' ? true : false;
  //   }
  //   return false;
  // };

  const handleEditClick = () => {
    setIsEditMode((prevMode) => !prevMode); //toggle
  };

  const handleOpen = async () => {
    //quando clicco sul pulsante apro la modal
    // setIsEditMode(false);
    setConfirmSaveOpenModal(true); //apro il popup
  };
  const [fullLoading, setFullLoading] = useState(false);
  const handleSaveSimulation = async (formData) => {
    //salvataggio simulazione
    setFullLoading(true);
    try {
      // const cardContent = JSON.parse(localStorage.getItem("orgData"));
      const cardContent = await loadOrgData();
      //console.log("dati che sto salvando", cardContent);
      if (!cardContent) {
        return;
      }
      const cardContentCompressed = (cardContent) => {
        try {
          const compressed = pako.gzip(JSON.stringify(cardContent));
          const base64 = btoa(
            Array.from(compressed)
              .map((byte) => String.fromCharCode(byte))
              .join("")
          );
          return base64;
        } catch (error) {
          console.error("errore compressione ", error);
          return null;
        }
      };
      // const lastUpdate=dayjs().format("YYYY-MM-DD HH:mm:ss");
      const payload = {
        // last_update:lastUpdate,
        card_content: cardContentCompressed(cardContent),
        ...formData, //campi non annidati
      };
      await hrPostNewSimulation(payload);
      showAlert(I18n.get("Simulation saved successfully"), "Success");
      setIsEditMode(false);
      //salvo i dati del grafico editato
    } catch (error) {
      console.log(error);
    } finally {
      setFullLoading(false);
    }
  };

  const handleFit = (event) => {
    if (chartRef.current) {
      chartRef.current.fit(event);
    }
  };

  const handleSwapLayout = useCallback((event) => {
    const nextIndex = (layoutIndex + 1) % 4;
    setLayoutIndex(nextIndex);
    localStorage.setItem("orgChartLayoutIndex", nextIndex);
    return nextIndex;
  });

  const handleExpandAll = () => {
    setFullLoading(true);
    setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.expandAll().render();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setFullLoading(false);
          });
        });
        handleFit();
      }
    }, 0);
  };

  const handleCollapseAll = () => {
    setFullLoading(true);
    setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.collapseAll().render();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setFullLoading(false);
          });
        });
        handleFit();
      }
    }, 0);
  };

  useEffect(() => {
    const isExporting = localStorage.getItem("isExporting") === "true";
    setHighlightedUser(!isExporting);
  }, [_]);

  // const handleSetCentered = useCallback(() => {
  //   if (chartRef.current) {
  //     const id = props.inputID;
  //     console.log(id);
  //     chartRef.current.clearHighlighting();
  //     chartRef.current
  //       .setCentered(id)
  //       .setHighlighted(id)
  //       .setUpToTheRootHighlighted(id)
  //       .render();
  //   }
  // });

  // const handleClearHighlighting = (event) => {
  //   event.preventDefault();
  //   if (chartRef.current) {
  //     chartRef.current.clearHighlighting();
  //   }
  // };

  // const idUser = String(
  //   orgData.find((node) => node.email === userMail.toLowerCase())?.id
  // );
  // console.log(idUser);

  // const highlightedUserNode = () => {
  //   if (idUser) {
  //     //per evidenziare utente loggato
  //     chartRef.current.setHighlighted(idUser).render();

  //     requestAnimationFrame(() => {
  //       //serve per cambiare il colore dell'evidenziazione
  //       const svg = d3Container.current.querySelector("svg");
  //       const nodes = svg?.querySelectorAll("g.node");
  //       nodes?.forEach((node) => {
  //         const foreignObject = node.querySelector("div[data-id");
  //         const rect = node.querySelector("rect");
  //         const nodeId = foreignObject?.getAttribute("data-id");
  //         if (String(nodeId) === idUser) {
  //           rect.setAttribute("stroke", "#3399ff");
  //           rect.setAttribute("height", "160px");
  //         }
  //       });
  //     });
  //   }
  // };
  useLayoutEffect(() => {
    const toggleDetailsCard = (nodeId) => {
      if(loggedUserData.reportsIds.length > 0 && loggedUserData.reportsIds.indexOf(nodeId) === -1) {
        handleClose();
      } else {
        handleShow();
      }
      setEmployeeId(nodeId);
    };

    const handleLayout = () => handleSwapLayout();

    if (data && d3Container.current) {
      try {
        chartRef.current
          .container(d3Container.current)
          .data(orgData) //data
          .nodeWidth((d) => 323)
          .nodeHeight((d) => 200)
          .layout(["left", "top", "right", "bottom"][layoutIndex])
          .compactMarginBetween((d) => 80)
          .onNodeClick((d) => {
            toggleDetailsCard(d.id);
          })
          .linkUpdate(function (d, i, arr) {
            //colore dei link
            d3.select(this).attr(
              "stroke",
              props.layerSpan.layer || props.layerSpan.span
                ? "#303439ff"
                : "#00449e"
            );
          })
          .buttonContent((node) => {
            return ReactDOMServer.renderToStaticMarkup(
              <CustomExpandButton {...node.node} />
            );
          })
          .nodeContent((d) => {
            return ReactDOMServer.renderToStaticMarkup(
              <div data-id={d.id} className="node-content">
                <CustomNodeContent
                  {...d}
                  orgData={orgData}
                  modifiedNodeIds={modifiedNodeIds}
                  layerSpan={props.layerSpan}
                  isPrivateHidden={isPrivateHidden}
                  privateVisibilityData={loggedUserData}
                  //highlightedUser={highlightedUser}
                />
              </div>
            );
          })
          .render();
        // if (idUser) {
        //   //per evidenziare utente loggato
        //   chartRef.current.setHighlighted(idUser).render();

        //   requestAnimationFrame(() => {
        //     //serve per cambiare il colore dell'evidenziazione
        //     const svg = d3Container.current.querySelector("svg");
        //     const nodes = svg?.querySelectorAll("g.node");
        //     nodes?.forEach((node) => {
        //       const foreignObject = node.querySelector("div[data-id");
        //       const rect = node.querySelector("rect");
        //       const nodeId = foreignObject?.getAttribute("data-id");
        //       if (String(nodeId) === idUser) {
        //         rect.setAttribute("stroke", "#3399ff");
        //         rect.setAttribute("height", "160px");
        //       }
        //     });
        //   });
        // }
      } catch (error) {
        setChartError(true);
        console.log(error);
      }
    }
    // document.querySelectorAll("rect.node-rect").forEach(r=>r.setAttribute("stroke", "green"));
    // setTimeout(()=>{
    //   if(d3Container.current){
    //     const svg=d3Container.current.querySelector("svg");
    //     if(svg){
    //       svg.style.backgroundImage= `url("/bg_chart.png")`;
    //       svg.style.backgroundSize="cover";
    //       svg.style.backgroundPosition="center";
    //       svg.style.backgroundRepeat="no-repeat";
    //       svg.style.width="100%";
    //       svg.style.height="100%";
    //     }
    //   }
    // }, 500);
  }, [orgData, layoutIndex, props.layerSpan, isPrivateHidden, loggedUserData]);

  useEffect(() => {
    let svg;

    //funzione per aprire il menu da tasto destro
    const handleNodeRightClick = (event) => {
      event.preventDefault();
      if (!isEditMode) return;

      const node = event.target.closest(".node-content");
      if (!node) {
        //console.log("nessun nodo sotto il cursore");
        return;
      }

      const nodeId = node.getAttribute("data-id");

      const nodeData = orgData.find((n) => n.id === nodeId);
      if (!nodeData) {
        //console.log("nodo non trovato nei dati");
        return;
      }
      setSelectedNode(nodeData); //imposta il nodo genitore

      setContextMenu({
        mouseX: event.clientX - 2,
        mouseY: event.clientY - 4,
        nodeData,
      });
    };
    const rafId = requestAnimationFrame(() => {
      const container = d3Container.current;
      if (!container) {
        //console.log("d3Container.current è null 2");
        return;
      }

      svg = container.querySelector("svg");
      if (!svg) {
        //console.log("svg non trovato dentro il container");
        return;
      }

      svg.addEventListener("contextmenu", handleNodeRightClick);
      //console.log("listner attaccato al grafico");
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (svg) {
        svg.removeEventListener("contextmenu", handleNodeRightClick);
        //console.log("listner rimosso");
      }
    };
  }, [orgData, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setContextMenu(null);
    }
  }, [isEditMode]);
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        90;
      setTableHeight(availableHeight);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const handleExportChange = () => {
      setRefreshKey((prev) => prev + 1);
    };
    window.addEventListener("export-status-changed", handleExportChange);
    return () =>
      window.removeEventListener("export-status-changed", handleExportChange);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const exportFlag = localStorage.getItem("export") === "true";
    };
    window.addEventListener("export-status-changed", handleStorageChange);
    return () => {
      window.removeEventListener("export-status-changed", handleStorageChange);
    };
  }, []);
  const [newData, setNewData] = useState([]);
  useEffect(() => {
    const handleChangeData = async (formData) => {
      try {
        const response = await loadOrgData();
        setNewData(response);
      } catch (error) {
        console.log(error);
      }
    };
    handleChangeData();
  }, [orgData]);

  return (
    <>
      <FullscreenLoader loading={fullLoading} />
      <Stack
        display="flex"
        direction="row"
        justifyContent="center"
        width="100%"
        gap={3}
        padding="1rem"
        position="relative"
        alignItems="center"
      >
        <ExpandAllButton onExpand={handleExpandAll} />
        <CollapseAllButton onCollapse={handleCollapseAll} />
        <PDFExportButton
          chartRef={d3Container}
          onFitChart={handleFit}
          period={period}
          fileName={"Chart"}
          setFullLoading={setFullLoading}
        />
        <FitButton onFitChart={handleFit} ref={d3Container} />
        <SwapButton onSwapLayout={handleSwapLayout} ref={d3Container} />
        <HidePrivateInfoButton
          isHidden={isPrivateHidden}
          onHidePrivate={handleHidePrivate}
          isBtnDisabled={loggedUserData.disablePrivateInfo}
        />
        <EditButton onEdit={handleEditClick} isEditMode={isEditMode} />
        {/* <FormControlLabel control={<Switch checked={isEditMode} onChange={(e)=>setIsEditMode(e.target.checked)} color="primary"/>}
        label="Edit mode"/> */}
        {isEditMode && <SaveButton open={handleOpen} />}
        {/* <SetCenteredButton
          centerChart={handleSetCentered}
          clearHighlighting={handleClearHighlighting}
          ref={d3Container}
        /> */}
      </Stack>
      {chartError ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            minHeight: tableHeight,
          }}
        >
          <Typography
            display="flex"
            alignItems={"center"}
            justifyContent={"center"}
          >
            {I18n.get(
              "The chart could not be created because of invalid or incomplete data."
            )}
          </Typography>
        </Box>
      ) : (
        <div
          ref={(node) => {
            d3Container.current = node;
            if (ref) ref.current = node;
          }}
        />
      )}

      {isEditMode && contextMenu !== null && (
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenAddModal}>
            <Add color="success" /> {I18n.get("Add node")}
          </MenuItem>
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenEditModal}>
            <Edit color="info" /> {I18n.get("Edit node")}
          </MenuItem>
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenMoveNode}>
            <MoveDown color="primary" />
            {I18n.get("Move node")}{" "}
          </MenuItem>
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenDeleteModal}>
            {" "}
            <Delete color="error" />
            {I18n.get("Delete node")}{" "}
          </MenuItem>
        </Menu>
      )}

      <ConfirmSaveSimulationModal
        open={confirmSaveOpenModal}
        onClose={() => setConfirmSaveOpenModal(false)}
        onSubmit={handleSaveSimulation}
      />
      <AddNodeModal
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          setSelectedNode(null);
        }}
        onSubmit={handleAddNode}
        parentNode={selectedNode}
        data={orgData}
      />
      <MoveNodeModal
        open={openMoveModal}
        onClose={() => setOpenMoveModal(false)}
        nodes={newData} //data
        selectedNode={selectedNode}
        onMoveNode={handleMove}
      />
      <EditNodeModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedNode(null);
        }}
        selectedNode={selectedNode} //nodo da modificare
        onSubmit={handleEditSubmit}
      />
      <DeleteNodeModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onSubmit={handleDeleteNode}
      />
      <AlertPopup
        open={openAlert}
        onClose={() => setOpenAlert(false)}
        alertTitle={alertTitle}
        alertMessage={alertMessage}
      />
      {cardShow && !isEditMode && (
        <EmployeeDetailsCard
          employees={data}
          employee={data.find((employee) => employee.id === employeeId)}
          handleClose={handleClose}
        />
      )}
    </>
  );
});

export default OrganizationalChart;
